import React, { useContext, useEffect, useState } from 'react';
import { UserManager } from 'oidc-client-ts';
import { useAuthStateStore } from '@annunciate/core';
import Config from '@annunciate/core/dist/utils/config';
import { Oidc, RegistrationContext } from '@annunciate/web/dist/context';
const { OIDC_AUTHORITY, OIDC_CLIENT_ID, OIDC_REDIRECT_URI, } = Config;
const isClient = typeof window !== 'undefined';
const { setToken, removeToken, setHasAuthInitialized, } = useAuthStateStore.getState();
export const AuthProvider = ({ children, silent = false, }) => {
    const [manager] = useState(isClient ? new UserManager({
        authority: OIDC_AUTHORITY || '',
        client_id: OIDC_CLIENT_ID || '',
        redirect_uri: OIDC_REDIRECT_URI + '/finalize',
        post_logout_redirect_uri: isClient ? window.origin : '',
        automaticSilentRenew: isClient && !silent,
        silent_redirect_uri: OIDC_REDIRECT_URI + '/silent',
        scope: "openid profile email",
    }) : undefined);
    const regCtx = useContext(RegistrationContext);
    useEffect(() => {
        if (silent)
            return;
        let isCancelled = false;
        async function updateAuthState(u, attemptSilent = false) {
            if (isCancelled)
                return;
            let user = u;
            if (!user)
                user = await manager?.getUser();
            const isExpired = !(user?.expired === false);
            if (user && !isExpired && user?.id_token) {
                setToken(user.id_token);
            }
            else if (user && isExpired) {
                removeToken();
                await manager?.removeUser();
            }
            else if (attemptSilent) {
                try {
                    await manager?.signinSilent();
                }
                catch (err) {
                    console.warn(err);
                }
            }
            if (attemptSilent) {
                setHasAuthInitialized(true);
            }
        }
        manager?.events.addUserLoaded(updateAuthState);
        manager?.events.addUserSignedIn(updateAuthState);
        manager?.events.addUserSignedOut(removeToken);
        manager?.events.addAccessTokenExpired(removeToken);
        updateAuthState(undefined, true);
        return () => {
            isCancelled = true;
            manager?.events.removeUserLoaded(updateAuthState);
            manager?.events.removeUserSignedIn(updateAuthState);
            manager?.events.removeUserSignedOut(removeToken);
            manager?.events.removeAccessTokenExpired(removeToken);
        };
    }, []);
    return (React.createElement(Oidc.Provider, { value: {
            signIn: async (redirect_uri, login_hint) => {
                try {
                    const postLoginRedirectUri = redirect_uri ? new URL(redirect_uri) : undefined;
                    if (postLoginRedirectUri && postLoginRedirectUri.pathname.startsWith('/login')) {
                        postLoginRedirectUri.pathname = '/';
                    }
                    await manager?.signinRedirect({
                        login_hint,
                        state: {
                            postLoginRedirectUri: postLoginRedirectUri?.toString(),
                        },
                    });
                }
                catch (error) {
                    console.error("Error signing in", error);
                }
            },
            signInCallback: async (url) => {
                const user = await manager?.signinRedirectCallback(url);
                return user;
            },
            signInSilentCallback: async (url) => manager?.signinSilentCallback(url),
            signOut: async (post_logout_redirect_uri) => {
                removeToken();
                return manager?.signoutRedirect({
                    post_logout_redirect_uri,
                });
            },
            register: regCtx.register,
        } }, children));
};
//# sourceMappingURL=AuthProvider.js.map